import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useState } from "react";

import type { DependencyRestoreResponse } from "../../../dashboard-client/types.gen";

import {
  listDependencyRestoreRequestsAsUserOptions,
  decideADependencyRestoreRequestAsUserMutation,
} from "../../../dashboard-client/@tanstack/react-query.gen";

export function DependenciesBlock({
  environmentId,
}: {
  environmentId: string;
}) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { refetchOnWindowFocus: false, retry: false },
        },
      }),
  );
  return (
    <QueryClientProvider client={client}>
      <DependencyRequests key={environmentId} environmentId={environmentId} />
    </QueryClientProvider>
  );
}

function DependencyRequests({ environmentId }: { environmentId: string }) {
  const [open, setOpen] = useState(false);
  const requests = useQuery({
    ...listDependencyRestoreRequestsAsUserOptions({
      query: { channel_environment_id: environmentId },
    }),
    enabled: open,
  });
  return (
    <details
      className="border border-slate-300/60 px-3 py-2 text-xs text-slate-600 dark:border-slate-700/60 dark:text-slate-300"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer font-mono text-[10px] font-bold uppercase tracking-wider">
        Dependencies · controlled restore
      </summary>
      {open && (
        <div className="mt-3 grid gap-3">
          <p>
            Actors run <code>arti-deps prepare</code> inside an attached npm
            project. An organization admin approves its exact lockfile;{" "}
            <code>arti-deps restore</code> installs offline without lifecycle
            scripts.
          </p>
          <p>
            The node supplies this tool without replacing the workspace image.
            Run <code>/run/arti/bin/arti-deps status</code> to check readiness;
            this does not submit a request or grant approval. General internet
            access and push permissions do not change.
          </p>
          <button
            type="button"
            disabled={requests.isFetching}
            className="justify-self-start font-mono text-[10px] font-bold uppercase text-emerald-700 disabled:opacity-50 dark:text-emerald-300"
            onClick={() => {
              void requests.refetch();
            }}
          >
            Refresh requests
          </button>
          {requests.isPending && <p>Loading dependency requests…</p>}
          {requests.isError ? (
            <p role="alert">
              Could not load approvals. Refresh or sign in again; no permissions
              have changed.
            </p>
          ) : (
            requests.data?.requests.map((request) => (
              <DependencyRequestCard
                key={`${request.id}:${request.revision}`}
                request={request}
                canApprove={requests.data.can_approve}
                onChanged={() => {
                  void requests.refetch();
                }}
              />
            ))
          )}
          {requests.data?.requests.length === 0 && (
            <p>
              No lockfile requests yet. No dependency downloads are authorized
              by default.
            </p>
          )}
        </div>
      )}
    </details>
  );
}

function DependencyRequestCard({
  request,
  canApprove,
  onChanged,
}: {
  request: DependencyRestoreResponse;
  canApprove: boolean;
  onChanged: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const decision = useMutation({
    ...decideADependencyRestoreRequestAsUserMutation(),
    onSuccess: onChanged,
  });
  const decide = (choice: "approve" | "revoke") =>
    decision.mutate({
      body: {
        decision: choice,
        request_id: request.id,
        revision: request.revision,
        ttl_hours: 24,
      },
    });
  return (
    <div className="grid min-w-0 gap-2 border-t border-slate-300/60 pt-3 dark:border-slate-700/60">
      <div className="break-all font-mono text-[10px]">
        {request.checkout_path}
      </div>
      <div className="font-semibold">
        {request.status} · {request.artifacts.length} registry artifacts
      </div>
      <details>
        <summary className="cursor-pointer">
          Review pinned packages and integrity hashes
        </summary>
        <div className="mt-2 max-h-48 overflow-auto text-[10px]">
          <p className="break-all">Lockfile SHA-256: {request.lockfile_hash}</p>
          {request.artifacts.map((artifact) => (
            <div
              key={`${artifact.url}:${artifact.integrity}`}
              className="my-2 break-all"
            >
              <strong>
                {artifact.name}@{artifact.version}
              </strong>
              <br />
              {artifact.url}
              <br />
              {artifact.integrity}
            </div>
          ))}
        </div>
      </details>
      {request.expires_at && (
        <p>Expires {new Date(request.expires_at).toLocaleString()}</p>
      )}
      {canApprove && (
        <>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            Approve only these pinned downloads for 24 hours, for all actors
            using this workspace. No scripts or publishing authorization.
          </label>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!confirmed || decision.isPending}
              className="font-semibold text-emerald-700 disabled:opacity-40 dark:text-emerald-300"
              onClick={() => decide("approve")}
            >
              Approve 24h
            </button>
            <button
              type="button"
              disabled={decision.isPending || request.status === "revoked"}
              className="font-semibold text-red-700 disabled:opacity-40 dark:text-red-300"
              onClick={() => decide("revoke")}
            >
              Revoke future downloads
            </button>
          </div>
        </>
      )}
      {decision.isError && (
        <p role="alert">Decision not confirmed. Refresh before retrying.</p>
      )}
    </div>
  );
}
